import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { select, Store } from '@ngrx/store';
import { combineLatest, fromEvent, Observable, Subject } from 'rxjs';
import { debounceTime, map, takeUntil } from 'rxjs/operators';
import { AccountDomainDeleteComponent } from '../../components/account-domain-delete/account-domain-delete.component';
import { DomainSetting } from '../../models';
import {
  deleteSettingsActions,
  getDomainSettingsAction,
  searchSettingsAction,
  toggleAccountSettingActions,
} from '../../store/actions/domain-settings.actions';
import { getDomainSettings, getSearchQuery, State } from '../../store/reducers';

enum ActionType {
  Edit,
  Remove,
}

@Component({
  selector: 'apsis-back-office-private-domain',
  templateUrl: './private-domain.component.html',
  styleUrls: ['./private-domain.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrivateDomainComponent implements OnInit, OnDestroy {
  public domainSettings$: Observable<DomainSetting[]>;
  public destroy$ = new Subject<void>();
  public ActionType = ActionType;

  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;

  constructor(
    private readonly store: Store<State>,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private modalService: NgbModal,
  ) {}

  ngOnInit(): void {
    fromEvent(this.searchInput.nativeElement, 'keyup')
      .pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(() =>
        this.store.dispatch(
          searchSettingsAction.search({
            searchQuery: this.searchInput.nativeElement.value,
          }),
        ),
      );

    this.domainSettings$ = combineLatest([
      this.store.pipe(select(getDomainSettings)),
      this.store.pipe(select(getSearchQuery)),
    ]).pipe(
      map(([domainSettings, searchQuery]) => {
        if (!searchQuery || !searchQuery.length) {
          return domainSettings;
        }
        domainSettings = domainSettings.filter((item) =>
          item.accountId.includes(searchQuery),
        );
        return domainSettings;
      }),
    );

    this.store.dispatch(getDomainSettingsAction.get());
  }

  ngOnDestroy() {
    this.destroy$.next();
  }

  actionClick(action: { actionType: ActionType; accountId: string }) {
    if (action.actionType === ActionType.Edit) {
      this.router.navigate([`edit/${action.accountId}`], {
        relativeTo: this.route,
      });
    } else {
      this.openConfirmationDialog(action.accountId);
    }
  }

  add() {
    this.router.navigate(['add'], { relativeTo: this.route });
  }

  toggleSetting({
    accountId,
    enabled,
  }: {
    accountId: string;
    enabled: boolean;
  }) {
    this.store.dispatch(
      toggleAccountSettingActions.toggle({ accountId, enabled }),
    );
  }

  public openConfirmationDialog(accountId: string) {
    const dialog = this.modalService.open(AccountDomainDeleteComponent, {
      backdrop: 'static',
      centered: true,
      keyboard: false,
      size: 'sm',
    });

    const { componentInstance } = dialog;
    componentInstance.accountId = accountId;
    componentInstance.delete.subscribe(() => {
      this.store.dispatch(
        deleteSettingsActions.delete({ accountId: accountId }),
      );
    });
  }
}
